import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import './App.css';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import RecipeBox from './pages/RecipeBox';
import CreateRecipe from './pages/CreateRecipe';
import RecipeDetail from './pages/RecipeDetail';
import ChatsPage from './pages/ChatsPage';
import ProfilePage from './pages/ProfilePage';
import BuzzPage from './pages/BuzzPage';
import BuzzHistoryPage from './pages/BuzzHistoryPage';
import SharedRecipeView from './pages/SharedRecipeView';
import ShoppingListPage from './pages/ShoppingListPage';
import BrowseRecipes from './pages/BrowseRecipes';
import FeedPage from './pages/FeedPage';
import HiveDetailPage from './pages/HiveDetailPage';
import AdminDashboard from './pages/AdminDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ImportShare from './pages/ImportShare';
import PaprikaImport from './pages/PaprikaImport';
import WaggleDanceGame from './pages/WaggleDanceGame';
import GroceriesScreen from './pages/GroceriesScreen';
import SupportPage from './pages/SupportPage';
import ContactPage from './pages/ContactPage';
import ProtectedRoute from './components/ProtectedRoute';
import { FeedProvider } from './contexts/FeedContext';

// Component to handle /join-hive?code=XXX or /join-hive?queen=XXX redirects
const JoinHiveRedirect = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const queenCode = searchParams.get('queen');
  if (queenCode) {
    return <Navigate to={`/feed?queen_invite=${queenCode}`} replace />;
  }
  return <Navigate to={code ? `/feed?invite=${code}` : '/feed'} replace />;
};

function App() {
  return (
    <HelmetProvider>
      <FeedProvider>
        <BrowserRouter>
          <Toaster 
            position="top-center"
            toastOptions={{
              style: {
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                color: '#92400e',
              },
          }}
        />
        <Routes>
          {/* Public landing page - homepage */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          {/* Public shared recipe view - no auth required */}
          <Route path="/shared/:sourceRecipeId" element={<SharedRecipeView />} />
          {/* Public variant view - specific user's version */}
          <Route path="/shared/variant/:recipeId" element={<SharedRecipeView variant />} />
          {/* Public buzz history - no auth required */}
          <Route path="/shared/:sourceRecipeId/buzz" element={<BuzzHistoryPage />} />
          <Route path="/recipe-box" element={<ProtectedRoute><RecipeBox /></ProtectedRoute>} />
          <Route path="/create" element={<ProtectedRoute><CreateRecipe /></ProtectedRoute>} />
          <Route path="/browse" element={<ProtectedRoute><BrowseRecipes /></ProtectedRoute>} />
          <Route path="/recipe/:recipeId" element={<ProtectedRoute><RecipeDetail /></ProtectedRoute>} />
          <Route path="/chats" element={<ProtectedRoute><ChatsPage /></ProtectedRoute>} />
          <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
          <Route path="/hive/:hiveId" element={<ProtectedRoute><HiveDetailPage /></ProtectedRoute>} />
          <Route path="/join-hive" element={<ProtectedRoute><JoinHiveRedirect /></ProtectedRoute>} />
          <Route path="/buzz" element={<ProtectedRoute><BuzzPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/shopping-list" element={<ProtectedRoute><ShoppingListPage /></ProtectedRoute>} />
          <Route path="/groceries" element={<ProtectedRoute><GroceriesScreen /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/import" element={<ImportShare />} />
          <Route path="/import-paprika" element={<ProtectedRoute><PaprikaImport /></ProtectedRoute>} />
          <Route path="/waggle-dance" element={<WaggleDanceGame />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </FeedProvider>
    </HelmetProvider>
  );
}

export default App;